
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User as AuthUser, Session as AuthSession } from "@supabase/supabase-js";

interface AuthState {
  session: AuthSession | null;
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    session: null,
    user: null,
    isLoading: true,
    isAuthenticated: false,
    error: null
  });

  useEffect(() => {
    // Function to fetch and set the current session
    async function getSession() {
      try {
        setAuthState(state => ({
          ...state,
          isLoading: true
        }));

        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        setAuthState({
          session: data.session,
          user: data.session?.user || null,
          isLoading: false,
          isAuthenticated: !!data.session,
          error: null
        });
      } catch (err: any) {
        console.error("Error getting auth session:", err);
        setAuthState({
          session: null,
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: err
        });
      }
    }
    
    // Initial session check
    getSession();
    
    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setAuthState({
          session,
          user: session?.user || null,
          isLoading: false,
          isAuthenticated: !!session,
          error: null
        });
      }
    );
    
    // Clean up subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return authState;
}
